/**
 * Server-resolved variant picker (snippets/variant-picker.liquid with server_mode).
 *
 * The page no longer ships product | json. Option values are rendered by Liquid from
 * product.options_with_values, and each selection asks Shopify for the matching variant through the
 * Section Rendering API: {product.url}?section_id=sw-variant-state&option_values=<ids>. The response is
 * a few KB (snippets/sw-variant-state-json.liquid) whatever the variant count, so products up to Shopify's
 * 2,048 variants work, where product.variants and product | json stop at 250.
 *
 * Same element name, markup and 'on:variant:change' event as assets/variant-picker.js (the legacy picker,
 * still used when theme settings > Variant picker is 'legacy'). The event detail also carries route,
 * sampleVariantId and labelHtml for pdp-blocks.js, product-form.js and variant-label.js.
 *
 * Required translation strings: noStock, noVariant, noSelectedVariant.
 */
if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = Array.from(this.querySelectorAll('.option-selector'));
      this.cache = new Map();
      this.requestId = 0;

      this.state = VariantPicker.readState(this.querySelector('[data-sw-variant-state]'));
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.matchVariant(this.state);
      this.preSelection = !this.variant && this.selectedOptions.includes(null);
      if (this.state) this.cache.set(this.getSelectedIds().join(','), Promise.resolve(this.state));

      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.addEventListener('pointerover', this.handlePointerOver.bind(this));
      this.addEventListener('focusin', this.handlePointerOver.bind(this));

      // Deferred so the quick-add drawer can finish setting up first.
      setTimeout(() => this.updateAddToCartButton());
    }

    /**
     * Parses a variant state script element.
     * @param {?Element} el - <script data-sw-variant-state>.
     * @returns {?object}
     */
    static readState(el) {
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    /**
     * Handles 'change' events from the option selectors.
     * @param {Event} evt - Event object.
     */
    async handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      VariantPicker.updateLabelText(evt);

      const requestId = ++this.requestId;
      this.setBusy(true);

      // The chosen variant comes from every selected value. Each later option's availability comes from the
      // values chosen before it only (like the legacy 'downward' method): Shopify returns no variant for a
      // combination that doesn't exist and would otherwise mark values against some other variant.
      const ids = this.getSelectedIdsByOption();
      const markRequests = this.optionSelectors.map((_, i) => (
        i > 0 && ids.slice(0, i).every(Boolean) ? this.fetchState(ids.slice(0, i)).catch(() => null) : null
      ));

      let state;
      let markStates;
      try {
        [state, ...markStates] = await Promise.all([this.fetchState(ids.filter(Boolean)), ...markRequests]);
      } catch (e) {
        if (requestId !== this.requestId) return;
        this.setBusy(false);
        // Could not reach Shopify: reload with the selection so the server renders the right variant.
        if (!this.selectedOptions.includes(null)) {
          window.location.href = `${this.dataset.url}?option_values=${this.getSelectedIds().join(',')}`;
        }
        return;
      }

      // A newer selection has started; drop this answer.
      if (requestId !== this.requestId) return;
      this.setBusy(false);
      this.applyState(state, evt, markStates);
    }

    /**
     * Applies a variant state to the page.
     * @param {object} state - Parsed variant state.
     * @param {Event} [evt] - The change event that led here.
     * @param {Array<?object>} [markStates] - Per option, the state that marks its values (null: leave unmarked).
     */
    applyState(state, evt, markStates = []) {
      this.state = state;
      this.variant = this.matchVariant(state);
      this.preSelection = !this.variant && this.selectedOptions.includes(null);

      this.updateAvailability(markStates);
      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }
      this.updateAddToCartButton();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();

      if (this.preSelection) return;
      const detail = {
        form: this.productForm,
        variant: this.variant || undefined,
        product: null,
        labelHtml: this.variant ? state.labelHtml || '' : ''
      };
      if (this.variant) {
        detail.route = state.route || '';
        detail.sampleVariantId = state.sampleVariantId || null;
        detail.finalSale = !!state.finalSale;
      }
      this.dispatchEvent(new CustomEvent('on:variant:change', { bubbles: true, detail }));
    }

    /**
     * Returns the state's variant when it matches every selected option, otherwise null. Shopify fills
     * unselected options with the first available value, and returns no variant for a combination
     * that does not exist.
     * @param {?object} state - Variant state.
     * @returns {?object}
     */
    matchVariant(state) {
      const variant = state && state.variant;
      if (!variant || this.selectedOptions.includes(null)) return null;
      return this.selectedOptions.every((value, i) => variant.options[i] === value) ? variant : null;
    }

    /**
     * Fetches (or reuses) the variant state for a set of option value ids.
     * @param {Array<string>} ids - Selected option value ids.
     * @returns {Promise<object>}
     */
    fetchState(ids) {
      const key = ids.join(',');
      if (this.cache.has(key)) return this.cache.get(key);

      const params = new URLSearchParams({ section_id: this.dataset.stateSection || 'sw-variant-state' });
      if (key) params.set('option_values', key);
      const request = fetch(`${this.dataset.stateUrl || this.dataset.url}?${params}`)
        .then((response) => {
          if (!response.ok) throw new Error(`Variant state ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const state = VariantPicker.readState(doc.querySelector('[data-sw-variant-state]'));
          if (!state) throw new Error('Variant state missing');
          return state;
        })
        .catch((error) => {
          this.cache.delete(key);
          throw error;
        });

      this.cache.set(key, request);
      return request;
    }

    /**
     * Prefetches the state an option would lead to, so the answer is usually ready by the click.
     * @param {Event} evt - pointerover or focusin event.
     */
    handlePointerOver(evt) {
      const optionEl = evt.target.closest && evt.target.closest('[data-option-value-id]');
      const labelEl = !optionEl && evt.target.closest && evt.target.closest('label[for]');
      const el = optionEl || (labelEl && this.querySelector(`#${CSS.escape(labelEl.htmlFor)}[data-option-value-id]`));
      if (!el || (navigator.connection && navigator.connection.saveData)) return;

      clearTimeout(this.prefetchTimer);
      this.prefetchTimer = setTimeout(() => {
        const index = this.optionSelectors.indexOf(el.closest('.option-selector'));
        if (index < 0) return;
        const ids = this.getSelectedIdsByOption();
        ids[index] = el.dataset.optionValueId;
        this.fetchState(ids.filter(Boolean)).catch(() => {});
        // ...and the state that marks the next option's values.
        if (index + 1 < ids.length && ids.slice(0, index + 1).every(Boolean)) {
          this.fetchState(ids.slice(0, index + 1)).catch(() => {});
        }
      }, 120);
    }

    /**
     * Disables add to cart while a selection is being resolved, so a stale variant can't be added.
     * @param {boolean} busy - Whether a request is in flight.
     */
    setBusy(busy) {
      this.toggleAttribute('aria-busy', busy);
      if (!this.productForm || this.section.querySelector('.js-product-form-main .has_call_now_btn')) return;
      const addBtn = this.productForm.querySelector('[name="add"]');
      if (addBtn && busy) addBtn.disabled = true;
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;
      if (this.section.querySelector('.js-product-form-main .has_call_now_btn')) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;
      const variantAvailable = Boolean(this.variant && this.variant.available);
      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else if (!this.variant) {
        // Same as the legacy picker: a combination that doesn't exist keeps the label, disabled.
        this.addBtn.textContent = this.addBtn.dataset.addToCartText;
      } else {
        this.addBtn.textContent = variantAvailable ? this.addBtn.dataset.addToCartText : theme.strings.noStock;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (el.classList.contains('custom-select__option')) {
        let em = el.querySelector('em');
        if (!available && !em) {
          em = document.createElement('em');
          em.className = 'pointer-events-none';
          el.appendChild(em);
        }
        if (em) {
          em.hidden = available;
          em.textContent = available ? '' : unavailableText;
        }
      } else if (el.nextElementSibling) {
        if (available) el.nextElementSibling.removeAttribute('title');
        else el.nextElementSibling.title = unavailableText;
      }
    }

    /**
     * Marks option values. Like the legacy 'downward' method, an option is marked only once every option
     * before it is chosen, against the state for those earlier values; the first option is never marked.
     * @param {Array<?object>} markStates - Per option, the state that marks its values.
     */
    updateAvailability(markStates) {
      if (this.dataset.showAvailability === 'false') return;

      this.optionSelectors.forEach((selector, index) => {
        const markState = markStates[index];
        const values = new Map(((markState && markState.options[index]) || []).map((v) => [String(v.id), v]));
        selector.querySelectorAll('[data-option-value-id]').forEach((optionEl) => {
          const value = values.get(optionEl.dataset.optionValueId);
          if (!markState || !value) {
            VariantPicker.updateOptionAvailability(optionEl, true, true);
          } else {
            VariantPicker.updateOptionAvailability(optionEl, value.exists, value.available);
          }
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;
      if (this.variant && this.variant.available && this.variant.inventory_management
        && this.state.formatted && this.state.formatted.inventory === 'none') {
        const backorderProdEl = this.backorder.querySelector('.backorder__product');
        const prodTitleEl = this.section.querySelector('.product-title');
        const variantTitle = this.variant.title.includes('Default') ? '' : ` - ${this.variant.title}`;
        backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
        hideBackorder = false;
      }
      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text (button pickers).
     * @param {object} evt - Event object.
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (!selector || selector.dataset.selectorType === 'dropdown') return;
      const colorText = selector.querySelector('.js-color-text');
      const value = evt.target.nextElementSibling && evt.target.nextElementSibling.querySelector('.js-value');
      if (colorText && value) colorText.textContent = value.textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;
        const variantMedia = this.mediaGallery.querySelector(`[data-media-id="${this.variant.featured_media.id}"]`);
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability = this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      if (this.variant) {
        const formatted = this.state.formatted || {};
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        this.price.querySelector('.price__current').innerHTML = formatted.price || '';
        if (priceWasEl) priceWasEl.innerHTML = formatted.compareAtPrice || '';

        if (this.variant.unit_price_measurement) {
          const { reference_value: value, reference_unit: unit } = this.variant.unit_price_measurement;
          this.price.querySelector('.unit-price__price').innerHTML = formatted.unitPrice || '';
          this.price.querySelector('.unit-price__unit').textContent = value === 1 ? unit : `${value} ${unit}`;
        }
        if (unitPriceEl) unitPriceEl.hidden = !this.variant.unit_price_measurement;

        this.price.classList.toggle('price--on-sale', this.variant.compare_at_price > this.variant.price);
        this.price.classList.toggle('price--sold-out', !this.variant.available);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = Boolean(this.variant || this.preSelection);
      const from = this.price.querySelector('.price__from');
      if (from) from.hidden = !this.preSelection;
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      const weight = this.variant && this.variant.weight > 0 && this.state.formatted ? this.state.formatted.weight : '';
      this.weights.forEach((el) => {
        el.textContent = weight || '';
        el.hidden = !weight;
      });
    }

    /**
     * Updates the barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      const barcode = this.variant && this.variant.barcode;
      this.barcodes.forEach((el) => {
        el.textContent = barcode || '';
        el.parentNode.hidden = !barcode;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;
      const sku = this.variant && this.variant.sku;
      this.sku.textContent = sku || '';
      this.sku.parentNode.hidden = !sku;
    }

    /**
     * Updates the URL with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Sets the forms' [name="id"] inputs to the id the cart should get: the variant's linked parent variant
     * (custom.parent_variant) when it has one, which the server puts in the state as formId.
     */
    updateVariantInput() {
      this.forms = this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');
      const id = (this.state && this.state.formId) || this.variant.id;
      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (!input) return;
        input.value = id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element for each selector.
     * @param {Element} selector - .option-selector element.
     * @returns {?Element}
     */
    static getSelectedEl(selector) {
      if (selector.dataset.selectorType === 'dropdown') {
        return selector.querySelector('.custom-select__option[aria-selected="true"][data-option-value-id]');
      }
      return selector.querySelector('input:checked');
    }

    /**
     * Gets the selected value of each option (null when not chosen yet).
     * @returns {Array}
     */
    getSelectedOptions() {
      return this.optionSelectors.map((selector) => {
        const el = VariantPicker.getSelectedEl(selector);
        if (!el) return null;
        return selector.dataset.selectorType === 'dropdown' ? el.dataset.value : el.value;
      });
    }

    /**
     * Gets the selected option value id of each option (null when not chosen yet).
     * @returns {Array}
     */
    getSelectedIdsByOption() {
      return this.optionSelectors.map((selector) => {
        const el = VariantPicker.getSelectedEl(selector);
        return el ? el.dataset.optionValueId || null : null;
      });
    }

    /**
     * Gets the chosen option value ids, for the option_values parameter.
     * @returns {Array<string>}
     */
    getSelectedIds() {
      return this.getSelectedIdsByOption().filter(Boolean);
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
